import angular from "angular";

import { Ajax, Language, Onboarding, Translations, WebSiteUserRole, AuthorizationContract, AuthorizationType } from "@salaxy/core";

import { OnboardingService, SessionService, SignatureMethod, SignatureService, UiHelpers } from "../../../services";

/**
 * Controller for showing the signing user interface for unsigned account.
 */
export class SigningController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$scope", "OnboardingService", "SessionService", "SignatureService", "$sce", "UiHelpers", "AjaxNg1", "$timeout"];

  /** If true (typically default), the signing person is also the contact person for the company */
  public isContactPersonSigner: boolean;

  /** If true, shows the component even if the Signing is already OK. Typically used only in test. */
  public showAlways: boolean;

  /** If true, component shows success popup */
  public showInPopup = false;

  /**
   * Will be true if the component has done authentication and determines that signature is already OK
   * If true, the component is not shown to the end user unless showAlways is set true.
   */
  public isSigningOk: boolean;

  /** True when the session is available (auth) AND the signature is required. */
  public isSigningRequired: boolean;

  /** URL for the PDF preview. Changes when saved, to assure that PDF is not cached.  */
  public pdfPreviewUrl: string;

  /** Binding for button, which calls startDigitalSignature(). */
  public isSigninStarted = false;

  constructor(
    private $scope: angular.IScope,
    private onboardingService: OnboardingService,
    private sessionService: SessionService,
    private signatureService: SignatureService,
    private $sce: angular.ISCEService,
    private uiHelpers: UiHelpers,
    private ajax: Ajax,
    private $timeout: angular.ITimeoutService,
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit = () => {
    this.init();

    this.sessionService.subscribe(this.$scope, () => {
      this.init();
    });
    this.isContactPersonSigner = true;
  };

  /** Saves the current onboarding model. */
  public save(): Promise<Onboarding> {
    if (this.isContactPersonSigner) {
      this.model.company.contact.email = this.model.signature.email;
      this.model.company.contact.telephone = this.model.signature.telephone;
    }
    return this.onboardingService.save()
      .then((onboarding) => {
        this.pdfPreviewUrl = null;
        return onboarding;
      });
  }

  /**
   * The onboarding model is provided by the onboarding service.
   */
  public get model(): Onboarding {
    if (this.onboardingService.model?.company) {
      this.onboardingService.model.company.language = Translations.getLanguage() as Language;
    }
    if (this.onboardingService.model?.person) {
      this.onboardingService.model.person.language = Translations.getLanguage() as Language;
    }
    return this.onboardingService.model;
  }

  /** Returns the PDF preview address for the authorization pdf. */
  public getPdfPreviewAddress() {
    if (!this.pdfPreviewUrl && this.model?.id) {
      this.pdfPreviewUrl = this.$sce.trustAsResourceUrl(this.onboardingService.getPdfPreviewAddress(true) + "&t=" + new Date().getTime() + "&language=" + this.model.company.language);
    }
    return this.pdfPreviewUrl;
  }

  /**
   * Starts Visma sign
   * If this.showInPopup is false, saves the model and starts the signature.
   * If this.showInPopup is true, saves the model, starts the signature and then opens success dialog.
   * @param authMethod Method of authentication (bank selection).
   * Only used for Persons (Telia methods), ignored for companies.
   */
  public startDigitalSignature(authMethod: string) {
    this.isSigninStarted = true;
    if (this.showInPopup) {
      this.save().then(() => {
        let skin = this.sessionService.getCurrentCss() || "";
        if (skin) {
          skin = `skin=${skin}&`;
        }

        const successUrl = this.sessionService.getOrigin() +
          `popup.html#settings/signing-success?${skin}lang=${Translations.getLanguage()}&id=${this.onboardingService.model?.id ?? ""}#access_token=${this.ajax.getCurrentToken()}`;
        const url = this.onboardingService.getDigitalSignUrl(authMethod, null, null, successUrl, successUrl.replace("/signing-success?", "/signing-cancel?") );
        // To prevent double-clicking
        this.$timeout(() => this.isSigninStarted = false, 2000);
        return this.uiHelpers.showExternalDialog("vismaSignPopup", url, {}, { width: 1080, height: 800 })
          .then((result) => {
            if (result?.action != "cancel") {
              return this.sessionService.checkSession();
            } else {
              return;
            }
          });
      });
    } else {
      this.uiHelpers.showLoading("SALAXY.NG1.SigningComponent.dialog.header", `Siirrytään allekirjoitukseen (${authMethod})`);
      // Saving is needed mainly for potential changes in name, e-mail and phone. AuthService, signerPersonalId and successUrl are passed in URL.
      this.save().then(() => {
        window.top.location.href = this.onboardingService.getDigitalSignUrl(authMethod);
      });
    }
  }

  /** List of supported Signature methods */
  public get methods() {
    return this.signatureService.getMethods();
  }

  /**
   * Returns the SignatureMethod for given value.
   * @param value - value for SignatureMethod, e.g. tupas-nordea.
   */
  public getMethod(value: string): SignatureMethod {
    const arr = this.methods.filter((x) => x.value === value);
    if (arr.length > 0) {
      return arr[0];
    }
    return null;
  }

  /**
   * Signs the authorization contract for the current user.
   * Creates a partner contract authorization and updates the session.
   * @returns Promise that resolves when the contract is signed and session is updated
   * @throws Error if the contract signing fails
   */
  public async signPartnerContract(): Promise<void> {
    const data: AuthorizationContract = {
      authorizationType: AuthorizationType.PartnerContract
    };
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    try {
      await this.ajax.postJSON("/onboarding/contract", data);
      await this.sessionService.checkSession();
    } catch (error) {
      console.error("Failed to sign partner contract:", error);
    } finally {
      loading.dismiss();
    }
  }

  private init() {
    this.isSigningOk = this.sessionService.isSigningOk();
    this.pdfPreviewUrl = null;
    this.isSigningRequired = !this.sessionService.isSigningOk();
    if (this.sessionService.isSessionChecked && this.sessionService.isAuthenticated) {
      const accountId = this.sessionService.session.currentAccount.id;
      if (this.onboardingService.model && this.onboardingService.model.company.resolvedId === accountId) {
        return;
      }

      // reset before get
      this.onboardingService.model = null;
      this.onboardingService.id = null;
      this.onboardingService.getOnboardingDataForAccount(this.sessionService.session.currentAccount.id).then(() => {
        if (this.onboardingService.model) {
          this.onboardingService.model.signature.isPep = null;
          this.onboardingService.model.company.ownership.isRequiredTracking = null;
          if (!this.onboardingService.model.id) {
            this.onboardingService.save();
          }
          return;
        } else {
          this.onboardingService.model = {
            accountType: WebSiteUserRole.Company,
            signature: {},
          };
          this.onboardingService.save();
        }
      });
    }
  }
}

